<app-page-header [pageTitle]="'PIVE'" [showCreateButton]="false" />

<!-- Qué se está viendo: cliente, establecimiento, año y búsqueda de donante. -->
<div class="mb-4 flex flex-wrap items-end gap-3" [formGroup]="filtros">
  <div class="w-72">
    <label for="pive-cliente" class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Cliente</label>
    <app-select id="pive-cliente" formControlName="cliente" placeholder="Elegí un cliente" [options]="clientesOptions" searchable />
  </div>
  <div class="w-64">
    <label for="pive-establecimiento" class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Establecimiento</label>
    <app-select id="pive-establecimiento" formControlName="establecimiento" placeholder="Todos los establecimientos" [options]="establecimientosOptions" [allowEmpty]="true" searchable />
  </div>
  <div class="w-32">
    <label for="pive-anio" class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Año</label>
    <app-select id="pive-anio" formControlName="anio" placeholder="Año" [options]="aniosOptions" />
  </div>
  <div class="w-56">
    <label for="pive-buscar" class="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Buscar donante (RP)</label>
    <input id="pive-buscar" type="text" placeholder="Ej.: 7438" formControlName="buscar"
      class="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 text-sm dark:border-gray-700 dark:text-white/90" />
  </div>
  <div role="group" aria-label="Ordenar la PIVE" class="inline-flex h-11 overflow-hidden rounded-lg ring-1 ring-inset ring-gray-300 dark:ring-gray-700">
    <button type="button" [class]="vista === 'opu' ? claseVistaActiva : claseVistaInactiva" [attr.aria-pressed]="vista === 'opu'" (click)="cambiarVista('opu')">
      Por OPU
    </button>
    <button type="button" [class]="vista === 'donante' ? claseVistaActiva : claseVistaInactiva" [attr.aria-pressed]="vista === 'donante'" (click)="cambiarVista('donante')">
      Por donante
    </button>
  </div>
</div>

@if (!filtros.controls.cliente.value) {
  <div class="rounded-lg border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
    Elegí un cliente para ver su historial de producción.
  </div>
} @else if (loading) {
  <p class="py-10 text-center text-sm text-gray-500">Cargando historial…</p>
} @else if (filas.length === 0) {
  <div class="rounded-lg border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
    Este cliente no tiene OPU en {{ filtros.controls.anio.value || 'el año elegido' }}.
  </div>
} @else {
  <!-- Los OPU del cliente: para ir a cargar el que falta. -->
  <div class="mb-5 rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-800 dark:bg-gray-900">
    <p class="mb-2 text-sm font-medium text-gray-700 dark:text-gray-200">OPU del año · los datos se cargan en cada OPU</p>
    <div class="flex flex-wrap gap-2">
      @for (opu of opus; track opu.id) {
        @let completo = opu.completas === opu.donantes;
        <a [routerLink]="['/pive', opu.id]"
          class="flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm hover:border-brand-400"
          [class.border-gray-200]="completo" [class.border-warning-300]="!completo" [class.bg-warning-50]="!completo"
          [matTooltip]="completo ? 'Todo cargado · abrir el OPU' : 'Abrir el OPU para cargar lo que falta'">
          <span class="font-medium text-gray-800 dark:text-white/90">{{ fecha(opu.fecha) }}</span>
          @if (opu.establecimiento) { <span class="text-xs text-gray-500">{{ opu.establecimiento }}</span> }
          @if (completo) {
            <app-badge color="success" size="sm">Completo</app-badge>
          } @else {
            <app-badge color="warning" size="sm">{{ opu.completas }} de {{ opu.donantes }} completas</app-badge>
          }
        </a>
      }
    </div>
  </div>

  @if (vista === 'opu') {
  <div class="space-y-4">
    @for (grupo of gruposOpuVisibles; track grupo.id) {
      @let r = resumenOpu(grupo);
      <section class="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <header class="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2 dark:border-gray-800 dark:bg-white/[0.03]">
          <a [routerLink]="['/pive', grupo.id]" class="text-base font-semibold text-gray-800 hover:text-brand-500 dark:text-white/90">
            OPU {{ fecha(grupo.fecha) }}
            @if (grupo.establecimiento) { <span class="text-sm font-normal text-gray-500">· {{ grupo.establecimiento }}</span> }
          </a>
          <span class="text-xs text-gray-500">
            {{ r.donantes }} donantes · {{ r.embriones }} embriones · producción {{ r.pctProduccion === null ? '—' : (r.pctProduccion | number:'1.1-1') + ' %' }}
          </span>
        </header>

        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="text-left text-xs text-gray-500 dark:text-gray-400">
              <tr class="border-b border-gray-100 dark:border-gray-800">
                <th class="px-3 py-2 font-medium">Donante</th>
                <th class="px-2 py-2 font-medium">Raza</th>
                <th class="px-2 py-2 font-medium">Toro</th>
                <th class="px-2 py-2 text-right font-medium">Ovocitos viables</th>
                <th class="px-2 py-2 text-right font-medium">Ovocitos totales</th>
                <th class="px-2 py-2 text-right font-medium">Cultivados</th>
                <th class="px-2 py-2 text-right font-medium">Clivados</th>
                <th class="px-2 py-2 text-right font-medium">Embriones</th>
                <th class="px-2 py-2 text-right font-medium">% de producción</th>
                <th class="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              @for (fila of grupo.filas; track fila.id) {
                @let n = numeros(fila);
                @let prod = produccion(fila);
                @let falta = faltante(fila);
                <tr class="border-b border-gray-100 dark:border-gray-800">
                  <td class="px-3 py-2 whitespace-nowrap font-medium text-gray-800 dark:text-white/90">{{ donante(fila) }}</td>
                  <td class="px-2 py-2 whitespace-nowrap">{{ raza(fila) || '—' }}</td>
                  <td class="px-2 py-2 whitespace-nowrap">{{ fila.toro_fecundador.nombre }}</td>
                  <td class="px-2 py-2 text-right">{{ fila.aspirado ? n.viables : '—' }}</td>
                  <td class="px-2 py-2 text-right">{{ fila.aspirado ? n.totales : '—' }}</td>
                  <td class="px-2 py-2 text-right">{{ n.cultivados ?? '—' }}</td>
                  <td class="px-2 py-2 text-right">{{ n.clivados ?? '—' }}</td>
                  <td class="px-2 py-2 text-right whitespace-nowrap">
                    <span class="font-semibold">{{ n.embriones ?? '—' }}</span>
                    @if (n.embriones) {
                      <span class="block text-xs text-gray-500">
                        @if (porDestino(fila, 'V')) { {{ porDestino(fila, 'V') }} vitrif. }
                        @if (porDestino(fila, 'V') && porDestino(fila, 'F')) { · }
                        @if (porDestino(fila, 'F')) { {{ porDestino(fila, 'F') }} frescos }
                      </span>
                    }
                  </td>
                  <td class="px-2 py-2 text-right">{{ prod === null ? '—' : (prod | number:'1.1-1') + ' %' }}</td>
                  <td class="px-2 py-2 whitespace-nowrap">
                    @if (falta) {
                      <a [routerLink]="['/pive', grupo.id]" class="inline-flex items-center gap-1" matTooltip="Abrir el OPU para cargarlo">
                        <app-badge color="warning" size="sm">{{ falta }}</app-badge>
                        <mat-icon class="text-base! text-brand-500">arrow_forward</mat-icon>
                      </a>
                    }
                  </td>
                </tr>
              }
            </tbody>
            <tfoot class="text-xs font-semibold text-gray-700 dark:text-gray-200">
              <tr class="bg-gray-50 dark:bg-white/[0.03]">
                <td class="px-3 py-1.5">TOTAL</td>
                <td class="px-2 py-1.5" colspan="2">{{ r.donantes }} donantes</td>
                <td class="px-2 py-1.5 text-right">{{ r.viables }}</td>
                <td class="px-2 py-1.5 text-right">{{ r.totales }}</td>
                <td class="px-2 py-1.5 text-right">{{ r.cultivados }}</td>
                <td class="px-2 py-1.5 text-right">{{ r.clivados }}</td>
                <td class="px-2 py-1.5 text-right">{{ r.embriones }}</td>
                <td colspan="2"></td>
              </tr>
              <tr class="bg-gray-50 text-gray-500 dark:bg-white/[0.03] dark:text-gray-400">
                <td class="px-3 py-1.5">PROMEDIO</td>
                <td colspan="2"></td>
                <td class="px-2 py-1.5 text-right" matTooltip="Viables sobre ovocitos totales">{{ r.pctViables === null ? '—' : (r.pctViables | number:'1.0-0') + ' %' }}</td>
                <td></td>
                <td class="px-2 py-1.5 text-right" matTooltip="Cultivados sobre ovocitos totales">{{ r.pctCultivados === null ? '—' : (r.pctCultivados | number:'1.0-0') + ' %' }}</td>
                <td class="px-2 py-1.5 text-right" matTooltip="Clivados sobre cultivados">{{ r.pctClivados === null ? '—' : (r.pctClivados | number:'1.0-0') + ' %' }}</td>
                <td class="px-2 py-1.5 text-right" matTooltip="Embriones por donante">{{ r.embrionesPorDonante === null ? '—' : (r.embrionesPorDonante | number:'1.1-1') }}</td>
                <td class="px-2 py-1.5 text-right" matTooltip="Embriones sobre cultivados">{{ r.pctProduccion === null ? '—' : (r.pctProduccion | number:'1.1-1') + ' %' }}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    } @empty {
      <p class="py-6 text-center text-sm text-gray-500">Ninguna donante coincide con «{{ filtros.controls.buscar.value }}».</p>
    }
  </div>
  } @else {
  <div class="space-y-4">
    @for (grupo of gruposVisibles; track grupo.donanteId) {
      @let r = resumen(grupo);
      <section class="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <header class="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-100 bg-gray-50 px-3 py-2 dark:border-gray-800 dark:bg-white/[0.03]">
          <span class="text-base font-semibold text-gray-800 dark:text-white/90">
            Donante {{ grupo.etiqueta }}
            @if (grupo.raza) { <span class="text-sm font-normal text-gray-500">· {{ grupo.raza }}</span> }
          </span>
          <span class="text-xs text-gray-500">
            {{ r.opus }} OPU · {{ r.embriones }} embriones · producción {{ r.pctProduccion === null ? '—' : (r.pctProduccion | number:'1.1-1') + ' %' }}
          </span>
        </header>

        <div class="overflow-x-auto">
          <table class="min-w-full text-sm">
            <thead class="text-left text-xs text-gray-500 dark:text-gray-400">
              <tr class="border-b border-gray-100 dark:border-gray-800">
                <th class="px-2 py-2 font-medium">Fecha</th>
                <th class="px-3 py-2 font-medium">Donante</th>
                <th class="px-2 py-2 font-medium">Raza</th>
                <th class="px-2 py-2 font-medium">Toro</th>
                <th class="px-2 py-2 text-right font-medium">Ovocitos viables</th>
                <th class="px-2 py-2 text-right font-medium">Ovocitos totales</th>
                <th class="px-2 py-2 text-right font-medium">Ovocitos cultivados</th>
                <th class="px-2 py-2 text-right font-medium">Clivados</th>
                <th class="px-2 py-2 text-right font-medium">Totales</th>
                <th class="px-2 py-2 text-right font-medium">% de producción</th>
              </tr>
            </thead>
            <tbody>
              @for (fila of grupo.filas; track fila.id) {
                @let n = numeros(fila);
                @let prod = produccion(fila);
                @let falta = faltante(fila);
                <tr class="border-b border-gray-100 dark:border-gray-800">
                  <td class="px-3 py-2 whitespace-nowrap">{{ fecha(fila.agendamiento.fecha) }}</td>
                  <td class="px-3 py-2 whitespace-nowrap font-medium text-gray-800 dark:text-white/90">
                    {{ donante(fila) }}
                    @if (falta) {
                      <a [routerLink]="['/pive', fila.agendamiento.id]" class="ml-1 inline-flex items-center gap-1" matTooltip="Abrir el OPU para cargarlo">
                        <app-badge color="warning" size="sm">{{ falta }}</app-badge>
                        <mat-icon class="text-base! text-brand-500">arrow_forward</mat-icon>
                      </a>
                    } @else {
                      <a [routerLink]="['/pive', fila.agendamiento.id]" class="ml-1 text-gray-400 hover:text-brand-500"
                        matTooltip="Abrir el OPU" aria-label="Abrir el OPU">
                        <mat-icon class="text-lg!">open_in_new</mat-icon>
                      </a>
                    }
                  </td>
                  <td class="px-2 py-2 whitespace-nowrap">{{ raza(fila) || '—' }}</td>
                  <td class="px-2 py-2 whitespace-nowrap">{{ fila.toro_fecundador.nombre }}</td>
                  <td class="px-2 py-2 text-right">{{ fila.aspirado ? n.viables : '—' }}</td>
                  <td class="px-2 py-2 text-right">{{ fila.aspirado ? n.totales : '—' }}</td>
                  <td class="px-2 py-2 text-right">{{ n.cultivados ?? '—' }}</td>
                  <td class="px-2 py-2 text-right">{{ n.clivados ?? '—' }}</td>
                  <td class="px-2 py-2 text-right whitespace-nowrap">
                    <span class="font-semibold">{{ n.embriones ?? '—' }}</span>
                    @if (n.embriones) {
                      <span class="block text-xs text-gray-500">
                        @if (porDestino(fila, 'V')) { {{ porDestino(fila, 'V') }} vitrif. }
                        @if (porDestino(fila, 'V') && porDestino(fila, 'F')) { · }
                        @if (porDestino(fila, 'F')) { {{ porDestino(fila, 'F') }} frescos }
                      </span>
                    }
                  </td>
                  <td class="px-2 py-2 text-right">{{ prod === null ? '—' : (prod | number:'1.1-1') + ' %' }}</td>
                </tr>
              }
            </tbody>
            <tfoot class="text-xs font-semibold text-gray-700 dark:text-gray-200">
              <tr class="bg-gray-50 dark:bg-white/[0.03]">
                <td class="px-3 py-1.5">TOTAL</td>
                <td class="px-2 py-1.5">{{ r.opus }} OPU</td>
                <td colspan="2"></td>
                <td class="px-2 py-1.5 text-right">{{ r.viables }}</td>
                <td class="px-2 py-1.5 text-right">{{ r.totales }}</td>
                <td class="px-2 py-1.5 text-right">{{ r.cultivados }}</td>
                <td class="px-2 py-1.5 text-right">{{ r.clivados }}</td>
                <td class="px-2 py-1.5 text-right">{{ r.embriones }}</td>
                <td></td>
              </tr>
              <tr class="bg-gray-50 text-gray-500 dark:bg-white/[0.03] dark:text-gray-400">
                <td class="px-3 py-1.5" colspan="4">PROMEDIO</td>
                <td class="px-2 py-1.5 text-right" matTooltip="Viables sobre ovocitos totales">{{ r.pctViables === null ? '—' : (r.pctViables | number:'1.0-0') + ' %' }}</td>
                <td></td>
                <td class="px-2 py-1.5 text-right" matTooltip="Cultivados sobre ovocitos totales">{{ r.pctCultivados === null ? '—' : (r.pctCultivados | number:'1.0-0') + ' %' }}</td>
                <td class="px-2 py-1.5 text-right" matTooltip="Clivados sobre cultivados">{{ r.pctClivados === null ? '—' : (r.pctClivados | number:'1.0-0') + ' %' }}</td>
                <td></td>
                <td class="px-2 py-1.5 text-right" matTooltip="Embriones sobre cultivados">{{ r.pctProduccion === null ? '—' : (r.pctProduccion | number:'1.1-1') + ' %' }}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
    } @empty {
      <p class="py-6 text-center text-sm text-gray-500">Ninguna donante coincide con «{{ filtros.controls.buscar.value }}».</p>
    }
  </div>
  }
}
